// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import Transition from 'presentations/views/components/atoms/Transition';
import Skeleton from 'presentations/views/components/atoms/Skeleton';
import Icon from 'presentations/views/components/atoms/Icon';
import Image from 'presentations/views/components/atoms/Image';
import Link from 'presentations/views/components/atoms/Link';
import IconButton from 'presentations/views/components/atoms/IconButton';
import Switch from 'presentations/views/components/atoms/Switch';
import {
  spacing,
  fontSize,
  borderRadius,
  fontWeight,
  mediaMax,
  isMediaMax,
} from 'presentations/views/styles';
import {
  black,
  blueGrayPale,
  gray01,
  gray03,
  gray04,
  primary,
  white,
  whiteFix,
} from 'presentations/views/styles/color';
import Logo from 'presentations/views/components/atoms/Logo';
import { LocalCampaign, setSourceTrack } from 'presentations/views/hooks/track';
import Dialog from 'presentations/views/components/atoms/Dialog';
import {
  helpPath,
  loginPath,
  privacyPolicyPath,
  signUpPath,
  termsPath,
} from 'presentations/routes/path';
import { SyntheticEvent, useCallback, useState } from 'react';
import SmallSignUpButton, {
  TitleType,
} from 'presentations/views/components/atoms/SmallSignUpButton';
import LoginButton from 'presentations/views/components/atoms/LoginButton';
import { Provider } from 'domains/models/account/providerType';
import { setEvent } from 'presentations/views/helpers/analytics';
import { imagePath } from 'presentations/assets';
import { useRouter } from 'next/router';
import RecommendGoogleScopeOnDialog from 'presentations/views/components/organisms/RecommendGoogleScopeOnDialog';

interface Props {
  className?: string;
  linkTo: string;
  color?: string;
  isShowViewerCalendar: boolean;
  isConnected: boolean;
  settingLink: string;
  onChangeIsShowViewerCalendar: (isShowViewerCalendar: boolean) => void;
  onClickSetting: any;
}

export default function Component(props: Props) {
  const version = 2;
  const t = translateInComponent(translations);
  const [anchor, setAnchor] = useState<HTMLDivElement | null>(null);
  const [isVideoLoading, setIsVideoLoading] = useState<boolean>(true);
  const [isLoadingMicrosoft, setIsLoadingMicrosoft] = useState<boolean>(false);
  const [isLoadingGoogle, setIsLoadingGoogle] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(1);
  const [googleSignUpUrl, setGoogleSignUpUrl] = useState<string | null>(null);
  const router = useRouter();
  const localeText = router.locale === 'ja' ? 'ja' : 'en';
  const isMobile = isMediaMax('tablet');

  const termsLink = `<a href="${termsPath()}" target="_blank">${t(
    'terms',
  )}</a>`;
  const privacyLink = `<a href="${privacyPolicyPath()}" target="_blank">${t(
    'privacy policy',
  )}</a>`;

  const onChangeTab = (index: number) => {
    setActiveIndex(index);
    const el = document.getElementsByClassName(
      `js-video-${index}`,
    )[0] as HTMLVideoElement;
    if (el) {
      el.currentTime = 0;
      el.play();
    }
  };

  const onVideoLoad = (e: SyntheticEvent<HTMLVideoElement>) => {
    const el = e.target as HTMLVideoElement;
    setIsVideoLoading(false);
  };

  const onVideoEnded = (e: SyntheticEvent<HTMLVideoElement>) => {
    const el = e.target as HTMLVideoElement;
    const index = Number(el.dataset.id || 1);
    let nextIndex = index === 1 ? 3 : 1;
    if (nextIndex > 3) nextIndex = 1;
    setTimeout(() => {
      onChangeTab(nextIndex);
    }, 500);
  };

  const onSubmitGoogleScope = () => {
    setIsLoadingGoogle(true);
    if (googleSignUpUrl) {
      window.location.href = googleSignUpUrl;
    }
  };

  const onCloseGoogleScope = () => {
    setGoogleSignUpUrl(null);
  };

  const onClick = (e: SyntheticEvent<HTMLDivElement>) => {
    if (props.isConnected) {
      props.onChangeIsShowViewerCalendar(!props.isShowViewerCalendar);
    } else {
      setAnchor(e.currentTarget);
      setEvent('click smart schedule');
      props.onChangeIsShowViewerCalendar(true);
    }
  };

  const onClose = () => {
    setAnchor(null);
    props.onChangeIsShowViewerCalendar(false);
  };

  const onTrackGoogle = useCallback((e: SyntheticEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    campaign.term = Provider.Google;
    setSourceTrack(campaign);
    setEvent('sign up on booking calendar', {
      version,
      provider: Provider.Google,
      position: 'calendar',
    });
    setGoogleSignUpUrl(e.currentTarget.href);
    setAnchor(null);
  }, []);

  const onTrackMicrosoft = useCallback(() => {
    campaign.term = Provider.Microsoft;
    setSourceTrack(campaign);
    setEvent('sign up on booking calendar', {
      version,
      provider: Provider.Microsoft,
      position: 'calendar',
    });
    setIsLoadingMicrosoft(true);
  }, []);

  const campaign: LocalCampaign = {
    medium: 'event_type',
  };

  return (
    <>
      <StyledConnectStatusContent>
        {props.isConnected && (
          <>
            <ConnectedIcon name="check-circle" themeColor={props.color} />
            {t('Calendar Connected')}
          </>
        )}
      </StyledConnectStatusContent>
      <CalendarConnect>
        <SwitchContainer>
          {!props.isConnected && (
            <CalendarImageList>
              <CalendarImageWrapper onClick={onClick}>
                <CalendarImage
                  src="account/google.svg"
                  alt="Google"
                  width={16}
                  height={16}
                  dpr={2}
                />
              </CalendarImageWrapper>
              <CalendarImageWrapper onClick={onClick}>
                <CalendarImage
                  src="account/microsoft.svg"
                  alt="Microsoft"
                  width={16}
                  height={16}
                  dpr={2}
                />
              </CalendarImageWrapper>
            </CalendarImageList>
          )}
          <SwitchLabel>{t('sub')}</SwitchLabel>
          <StyledSwitch
            onChange={onClick}
            color={props.color}
            isChecked={props.isShowViewerCalendar}
          />
          <SwitchHelpLink
            href={helpPath('7155115', router.locale)}
            target="_blank"
          >
            <SwitchHelpIcon name="help" />
          </SwitchHelpLink>
        </SwitchContainer>
        {props.isConnected && (
          <CalendarSettingIconButton
            href={props.settingLink}
            onClick={props.onClickSetting}
          >
            <CalendarSettingIcon name="settings" />
          </CalendarSettingIconButton>
        )}
      </CalendarConnect>

      {anchor && (
        // <StyledModal isOpen onChange={onClose}>
        <StyledDialog
          isOpen
          onClose={onClose}
          titleComponent={
            <>
              <LeftNavigationItem />
              <ModalHeadline>
                <ModalLogo />
              </ModalHeadline>
              <RightNavigationItem>
                <CloseIconButton onClick={onClose}>
                  <CloseIcon name="close" />
                </CloseIconButton>
              </RightNavigationItem>
            </>
          }
        >
          <ModalContainer>
            <ModalDescription>{t('Text')}</ModalDescription>
            <VideoList>
              {isVideoLoading && (
                <VideoLoading>
                  <Skeleton width={100} />
                </VideoLoading>
              )}
              {[1, 3].map((index) => (
                <Transition
                  key={index}
                  name="fade"
                  in={activeIndex === index}
                  timeout={1000}
                >
                  <VideoContainer isActive={index === activeIndex}>
                    <VideoItem
                      data-id={index}
                      preload={index === 1 ? 'auto' : 'none'}
                      className={`js-video-${index}`}
                      src={imagePath(
                        `pages/top/202306/slide0${index}_${localeText}.mp4`,
                      )}
                      onEnded={onVideoEnded}
                      onPlay={onVideoLoad}
                      autoPlay
                      muted
                      width={460}
                      playsInline
                      // height={350}
                    />
                    <TitleContainer>
                      <IconLabel>Point</IconLabel>
                      <PointTitle>{t(`guest_${index}_title`)}</PointTitle>
                    </TitleContainer>
                  </VideoContainer>
                </Transition>
              ))}
            </VideoList>
            <Description>{t('Text2')}</Description>
            <ConnectButtonContainer>
              {isMobile ? (
                <>
                  <MobileSignUpButton
                    service={Provider.Google}
                    titleType={TitleType.SignUp}
                    href={signUpPath({
                      provider: Provider.Google,
                      to: props.linkTo,
                      skipOnboard: 1,
                      hiddenPasswordProvider: 1,
                    })}
                    onClick={onTrackGoogle}
                    isDisabled={isLoadingGoogle}
                  />
                  <MobileSignUpButton
                    service={Provider.Microsoft}
                    titleType={TitleType.SignUp}
                    href={signUpPath({
                      provider: Provider.Microsoft,
                      to: props.linkTo,
                      skipOnboard: 1,
                      hiddenPasswordProvider: 1,
                    })}
                    onClick={onTrackMicrosoft}
                    isDisabled={isLoadingMicrosoft}
                  />
                </>
              ) : (
                <>
                  <SignUpButton
                    service={Provider.Google}
                    variant="signup"
                    href={signUpPath({
                      provider: Provider.Google,
                      to: props.linkTo,
                      skipOnboard: 1,
                      hiddenPasswordProvider: 1,
                    })}
                    onClick={onTrackGoogle}
                    isDisabled={isLoadingGoogle}
                  />
                  <SignUpButton
                    service={Provider.Microsoft}
                    variant="signup"
                    href={signUpPath({
                      provider: Provider.Microsoft,
                      to: props.linkTo,
                      skipOnboard: 1,
                      hiddenPasswordProvider: 1,
                    })}
                    onClick={onTrackMicrosoft}
                    isDisabled={isLoadingMicrosoft}
                  />
                </>
              )}
            </ConnectButtonContainer>
            <AuthDescription
              dangerouslySetInnerHTML={{
                __html: t('terms description', { termsLink, privacyLink }),
              }}
            />
            <LoginSwitchContainer>
              <LoginSwitchIcon name="chevron-right" />
              <LoginSwitchLink href={loginPath({ to: props.linkTo })}>
                {t('already have an account')}
              </LoginSwitchLink>
            </LoginSwitchContainer>
          </ModalContainer>
        </StyledDialog>
      )}
      {googleSignUpUrl && (
        <RecommendGoogleScopeOnDialog
          onSubmit={onSubmitGoogleScope}
          onClose={onCloseGoogleScope}
          isLoading={isLoadingGoogle}
        />
      )}
    </>
  );
}

const PointTitle = styled.div`
  font-size: ${fontSize(20)};
  color: ${black};
  font-weight: ${fontWeight('bold')};
  ${mediaMax.tablet} {
    font-size: ${fontSize(18)};
  }
`;
const StyledDialog = styled(Dialog)`
  min-width: 640px;
  display: flex;
  flex-direction: column;
  align-items: center;
  ${mediaMax.tablet} {
    min-width: initial;
  }
`;
const ModalContainer = styled.div`
  /* background-color: ${white};
  border-radius: ${borderRadius(16)};
  border: 1px solid ${gray04};
  padding: ${spacing(24)};
  ${mediaMax.tablet} {
    padding: ${spacing(12)};
    margin: ${spacing(12)};
    display: flex;
    flex-direction: column;
    justify-content: center;
  } */
`;

const ModalHeadline = styled.div`
  font-weight: ${fontWeight('bold')};
  position: sticky;
  top: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  font-size: ${fontSize(24)};
  margin: ${spacing(8)} auto;
  /* margin-bottom: ${spacing(24)}; */
  ${mediaMax.tablet} {
    font-size: ${fontSize(18)};
  }
`;
const ModalDescription = styled.p`
  margin-bottom: ${spacing(24)};
  text-align: center;
  max-width: 560px;
`;
const ModalLogo = styled(Logo)`
  width: 130px;
  margin-right: ${spacing(8)};
  ${mediaMax.tablet} {
    width: 108px;
  }
`;

const ConnectButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: ${spacing(4)};
  margin-bottom: ${spacing(24)};
  flex-shrink: 0;
`;

const MobileSignUpButton = styled(SmallSignUpButton)`
  margin-right: ${spacing(12)};
  span {
    color: ${black};
    letter-spacing: initial;
    font-size: ${fontSize(13)};
  }
  :last-of-type {
    margin-right: 0;
  }
`;
const SignUpButton = styled(LoginButton)`
  min-width: auto;
  margin-left: ${spacing(16)};
  span {
    letter-spacing: 0.02em;
  }
`;
const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing(16)};
`;
const IconLabel = styled.div`
  height: fit-content;
  color: ${whiteFix};
  background-color: ${primary};
  border-radius: 50px;
  font-weight: ${fontWeight('bold')};
  padding: ${spacing(4)} ${spacing(8)};
  font-size: ${fontSize(12)};
  margin-right: ${spacing(8)};
`;
const AuthDescription = styled.div`
  padding: 0 ${spacing(24)};
  font-size: ${fontSize(11)};
  text-align: center;
`;

const Description = styled.div`
  margin-bottom: ${spacing(12)};
  max-width: 600px;
  padding: 0 ${spacing(24)};
  font-size: ${fontSize(13)};
  text-align: center;
`;

const VideoList = styled.div`
  flex: 1;
`;
const VideoContainer = styled.div<{ isActive: boolean }>`
  display: ${(props) => (props.isActive ? 'flex' : 'none')};
  flex-direction: column;
  align-items: center;
  height: 100%;
  overflow: hidden;
  position: relative;
`;
const VideoItem = styled.video`
  display: block;
  width: 460px;
  /* height: auto; */
  margin-bottom: ${spacing(16)};
  ${mediaMax.tablet} {
    width: 100%;
  }
`;
const VideoLoading = styled.div`
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledSwitch = styled(Switch)<{ color?: string }>`
  .MuiSwitch-switchBase.Mui-checked {
    color: ${(props) => props.color};
  }
  .Mui-checked + .MuiSwitch-track {
    background-color: ${(props) => props.color} !important;
  }
`;

const StyledConnectStatusContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: ${fontSize(12)};
  font-weight: ${fontWeight('bold')};
  ${mediaMax.tablet} {
    display: none;
    /* font-size: ${fontSize(11)};
    letter-spacing: -0.01em; */
  }
`;
const ConnectedIcon = styled(Icon)<{ themeColor?: string }>`
  margin-right: ${spacing(4)};
  width: 20px !important;
  height: 20px !important;
  color: ${(props) => props.themeColor || primary};
`;
const CalendarSettingIconButton = styled(IconButton)``;
const CalendarSettingIcon = styled(Icon)`
  width: 20px !important;
  height: 20px !important;
`;
const SwitchContainer = styled.div`
  border-radius: ${borderRadius(8)};
  padding: 0 ${spacing(16)};
  background-color: ${blueGrayPale};
  font-size: ${fontSize(13)};
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    padding: 0 ${spacing(12)};
  }
`;
const SwitchLabel = styled.div`
  margin-right: ${spacing(4)};
  padding: 0;
  margin: 0;
  ${mediaMax.tablet} {
    font-size: ${fontSize(11)};
    letter-spacing: -0.01em;
  }
`;
const SwitchHelpLink = styled(Link)`
  display: flex;
  align-items: center;
`;
const SwitchHelpIcon = styled(Icon)`
  color: ${gray01};
  width: 18px !important;
  height: 18px !important;
`;
const CalendarConnect = styled.div`
  display: flex;
  align-items: center;
`;
const CalendarImageList = styled.div`
  margin-right: ${spacing(8)};
  display: flex;
  align-items: center;
`;
const CalendarImageWrapper = styled.div`
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid ${gray04};
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${white};
  margin-right: -${spacing(8)};
  :last-of-type {
    margin-right: 0;
  }
`;
const CalendarImage = styled(Image)`
  display: block;
`;

const LeftNavigationItem = styled.div`
  width: 80px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;
const RightNavigationItem = styled.div`
  width: 80px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;
const CloseIconButton = styled(IconButton)`
  color: ${gray03};
`;
const CloseIcon = styled(Icon)``;
const LoginSwitchContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${fontSize(13)};
  margin-top: ${spacing(16)};
`;
const LoginSwitchIcon = styled(Icon)`
  color: ${primary};
`;
const LoginSwitchLink = styled.a`
  color: ${black};
  display: flex;
  align-items: center;
  line-height: 1;
`;
